import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { GLOBAL_FILTER_EVENTS } from '@/features/global-filter/constants/events';
import type { RefreshInterval } from '@/features/header/constants/labels';
import { CopyFilterIdButton } from './CopyFilterIdButton';
import { ClearFilterButton } from './ClearFilterButton';
import { ToggleViewButton } from './ToggleViewButton';
import { RefreshIntervalButton } from './RefreshIntervalButton';
import { OpenFilterButton } from './OpenFilterButton';
import { parseHtmlLabel } from '@/features/header/utils/labelParser';
import { useFilterBarTooltip } from '@/features/header/hooks/useFilterBarTooltip';
import { FilterBarTooltip } from './FilterBarTooltip';
import {
  BdsIcon,
  BdsThemeProvider,
} from '@/core/constants/webComponents';
import DOMPurify from 'dompurify';

import { useSidebarOpen } from '@/features/header/hooks/useSidebarOpen';
import './FilterBar.scss';

export interface FilterBarProps {
  filterLabels?: string[];
  hideTopBar?: boolean;
  hideCopyFilterIdButton?: boolean;
  hideClearFilterButton?: boolean;
  lockRefreshInterval?: boolean;
  onOpenFilter?: () => void;
}

const REFRESH_INTERVALS: RefreshInterval[] = [3, 6, 15, 30, 60, 0];

const EVENT_NAMES = {
  COPY_ID: 'mfe-filter-bar-copy-id',
  CLEAR: 'mfe-filter-bar-clear',
  TOGGLE_VIEW: 'mfe-filter-bar-toggle-view',
  REFRESH_INTERVAL: 'mfe-filter-bar-refresh-interval',
  OPEN_FILTER: 'mfe-filter-bar-open-filter',
  UPDATE_FILTER_LABELS: 'mfe-filter-bar-update-labels',
} as const;

export const FilterBar: React.FC<FilterBarProps> = ({
  filterLabels: propFilterLabels = [],
  hideTopBar = false,
  hideCopyFilterIdButton = false,
  hideClearFilterButton = false,
  lockRefreshInterval = false,
  onOpenFilter,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFilterVisible, setIsFilterVisible] = useState(true);
  const [activeInterval, setActiveInterval] = useState<RefreshInterval>(0);
  const isSidebarOpen = useSidebarOpen();
  const [externalFilterLabels, setExternalFilterLabels] = useState<string[]>([]);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const { tooltip, showTooltip, hideTooltip, cancelHide } = useFilterBarTooltip();

  // Combina labels das props com as recebidas via evento externo
  const allFilterLabels = [...propFilterLabels, ...externalFilterLabels];

  const actualIsSidebarOpen = hideTopBar ? false : isSidebarOpen;

  useEffect(() => {
    const handleFilterClosed = () => setIsFilterModalOpen(false);
    globalThis.addEventListener(GLOBAL_FILTER_EVENTS.CLOSED, handleFilterClosed);
    return () => globalThis.removeEventListener(GLOBAL_FILTER_EVENTS.CLOSED, handleFilterClosed);
  }, []);

  useEffect(() => {
    const handleExternalLabelsUpdate = (e: Event) => {
      const customEvent = e as CustomEvent<{ labels: string[] | string }>;
      const rawLabels = customEvent.detail?.labels;

      if (!rawLabels) return;

      try {
        if (typeof rawLabels === 'string') {
          const parsed = JSON.parse(rawLabels);
          if (Array.isArray(parsed)) {
            setExternalFilterLabels(parsed);
          }
        } else if (Array.isArray(rawLabels)) {
          setExternalFilterLabels(rawLabels);
        }
      } catch (error) {
        console.error('[FilterBar] Error parsing labels:', error);
      }
    };

    document.addEventListener(EVENT_NAMES.UPDATE_FILTER_LABELS, handleExternalLabelsUpdate);
    return () => document.removeEventListener(EVENT_NAMES.UPDATE_FILTER_LABELS, handleExternalLabelsUpdate);
  }, []);


  const emitEvent = (eventName: string, detail?: Record<string, unknown>) => {
    const target = containerRef.current ?? document;
    target.dispatchEvent(new CustomEvent(eventName, { detail, bubbles: true, composed: true }));
  };

  const handleCopyId = () => {
    emitEvent(EVENT_NAMES.COPY_ID);
  };

  const handleClearFilter = () => {
    emitEvent(EVENT_NAMES.CLEAR);
  };

  const handleToggleView = () => {
    const next = !isFilterVisible;
    setIsFilterVisible(next);
    emitEvent(EVENT_NAMES.TOGGLE_VIEW, { isVisible: next });
  };

  const handleRefreshClick = () => {
    const currentIndex = REFRESH_INTERVALS.indexOf(activeInterval);
    const nextIndex = (currentIndex + 1) % REFRESH_INTERVALS.length;
    const nextInterval = REFRESH_INTERVALS[nextIndex];
    setActiveInterval(nextInterval);
    emitEvent(EVENT_NAMES.REFRESH_INTERVAL, { interval: nextInterval });
  };

  const handleOpenFilter = () => {
    setIsFilterModalOpen(true);
    if (onOpenFilter) {
      onOpenFilter();
    } else {
      emitEvent(EVENT_NAMES.OPEN_FILTER);
    }
  };

  return (
    <BdsThemeProvider theme="dark" style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
      <div
        className={`filter-bar${actualIsSidebarOpen ? ' filter-bar--sidebar-open' : ' filter-bar--full-width'}`}
        ref={containerRef}
        data-testid="filter-bar"
      >
        <div
          className="filter-bar__filter-display"
          data-testid="filter-bar-display"
        >
          <BdsIcon
            class="filter-bar__filter-icon__calendar"
            name="calendar"
            theme="outline"
            size="x-small"
            color="surface-0"
          />
          <div className={`filter-bar__filter-list ${isFilterVisible ? 'open' : ''}`}>
            {allFilterLabels.map((filter) => {
              const { label, value, originalLabel, isTruncated, hiddenValues, overflowCount } = parseHtmlLabel(filter, t);
              const sanitizedValue = DOMPurify.sanitize(value, {
                ALLOWED_TAGS: ['b', 'strong', 'i', 'em', 'u', 'span', 'br'],
                ALLOWED_ATTR: ['class'],
              });
              return (
                <React.Fragment key={originalLabel}>
                  <span className="filter-bar__filter-item">
                    <strong className="filter-bar__filter-label">
                      {label}
                      {isFilterVisible && <>{':'}&nbsp;</>}
                    </strong>
                    {isFilterVisible && (
                      <>
                        <span
                          className="filter-bar__filter-value"
                          dangerouslySetInnerHTML={{ __html: sanitizedValue }}
                        />
                        {isTruncated && (
                          <button
                            type="button"
                            className="filter-bar__overflow-indicator"
                            onMouseEnter={(e) => {
                              const rect = e.currentTarget.getBoundingClientRect();
                              showTooltip(rect.left + rect.width / 2, rect.bottom + 6, hiddenValues);
                            }}
                            onFocus={(e) => {
                              const rect = e.currentTarget.getBoundingClientRect();
                              showTooltip(rect.left + rect.width / 2, rect.bottom + 6, hiddenValues);
                            }}
                            onMouseLeave={hideTooltip}
                            onBlur={hideTooltip}
                          >
                            {` (+${overflowCount})`}
                          </button>
                        )}
                      </>
                    )}
                  </span>
                  {!isFilterVisible && filter !== allFilterLabels.at(-1) && (
                    <span className="filter-bar__filter-separator" />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        <div className="filter-bar__actions">
          {!hideCopyFilterIdButton && <CopyFilterIdButton onClick={handleCopyId} showTooltip={showTooltip} hideTooltip={hideTooltip} />}
          {!hideClearFilterButton && <ClearFilterButton onClick={handleClearFilter} showTooltip={showTooltip} hideTooltip={hideTooltip} />}
          <ToggleViewButton isFilterVisible={isFilterVisible} onClick={handleToggleView} showTooltip={showTooltip} hideTooltip={hideTooltip} />
          <RefreshIntervalButton activeInterval={lockRefreshInterval ? 0 : activeInterval} disabled={lockRefreshInterval} onClick={handleRefreshClick} showTooltip={showTooltip} hideTooltip={hideTooltip} />
          <OpenFilterButton
            onClick={handleOpenFilter}
            showTooltip={showTooltip}
            hideTooltip={hideTooltip}
            isActive={isFilterModalOpen}
          />
        </div>
      </div>
      <FilterBarTooltip
        visible={tooltip.visible}
        left={tooltip.left}
        top={tooltip.top}
        items={tooltip.items}
        rightAligned={tooltip.rightAligned}
        onMouseEnter={cancelHide}
        onMouseLeave={hideTooltip}
      />
      </BdsThemeProvider>
  );
};
